// Guides, kit file downloads and search (plan §4, T34 and T35). Its own factory over the shared
// http layer, so the web app adds it without changing the core client.
import { API_BASE_PATH, createHttp, type HttpOptions } from './http.ts';
import { pathSegment } from './paths.ts';
import {
  guidePageSchema,
  guideSchema,
  kitFilePageSchema,
  searchPageSchema,
  type Guide,
  type GuidePage,
  type KitFilePage,
  type SearchPage,
} from './schemas/library.ts';

export * from './schemas/library.ts';

export interface Paging {
  readonly page?: number;
  readonly pageSize?: number;
}

export interface LibraryApi {
  /** GET /guides: guides this person may read (team, plus Leads guides for Leads roles). */
  listGuides(paging?: Paging, signal?: AbortSignal): Promise<GuidePage>;
  /** GET /guides/:slug: one guide as a block tree. */
  getGuide(slug: string, signal?: AbortSignal): Promise<Guide>;
  /** GET /files: kit files this person may download. */
  listFiles(paging?: Paging, signal?: AbortSignal): Promise<KitFilePage>;
  /** The download URL for a plain link: the browser sends the session cookie and saves the file. */
  downloadUrl(id: number): string;
  /** GET /search?q=: results this person may see. */
  search(q: string, paging?: Paging, signal?: AbortSignal): Promise<SearchPage>;
}

function query(params: Readonly<Record<string, string | number | undefined>>): string {
  const entries = Object.entries(params).filter(
    (e): e is [string, string | number] => e[1] !== undefined,
  );
  const qs = new URLSearchParams(entries.map(([k, v]) => [k, String(v)])).toString();
  return qs === '' ? '' : `?${qs}`;
}

export function createLibraryApi(options: HttpOptions = {}): LibraryApi {
  const http = createHttp(options);
  const base = options.baseUrl ?? API_BASE_PATH;
  const paged = (paging: Paging = {}) => query({ page: paging.page, pageSize: paging.pageSize });
  return {
    listGuides: (paging, signal) =>
      http.json({ method: 'GET', path: `/guides${paged(paging)}`, signal }, guidePageSchema),
    getGuide: (slug, signal) =>
      http.json({ method: 'GET', path: `/guides/${pathSegment(slug)}`, signal }, guideSchema),
    listFiles: (paging, signal) =>
      http.json({ method: 'GET', path: `/files${paged(paging)}`, signal }, kitFilePageSchema),
    downloadUrl: (id) => `${base}/files/${pathSegment(id)}/download`,
    search: (q, paging = {}, signal) =>
      http.json(
        {
          method: 'GET',
          path: `/search${query({ q, page: paging.page, pageSize: paging.pageSize })}`,
          signal,
        },
        searchPageSchema,
      ),
  };
}
